import { useEffect, useState } from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import { CURRENT_SURFACE_CSS_VAR } from '@atlaskit/tokens/constants';
import type { ThemeState } from '@atlaskit/tokens/theme-state';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import { useMemoizedStateObject } from '../utils';

/**
 * Host theme and current surface colour for the worker runtime's bridge context, following what
 * `Iframe` supplies to the iframe v2 runtime: the theme is deep-equality memoized, and the surface
 * colour starts `null` and is read in an effect whenever the theme changes. Passed to
 * `useBridgeContext` as `theme` / `surfaceColor`, which keeps the app's context current and emits
 * `FORGE_CORE_THEME_CHANGED` on a change, exactly as for the iframe runtime.
 *
 * Nothing is pushed to restyle the app: that goes through `bridgeControls.setTheme`, which only the
 * iframe runtime wires up. Unlike the iframe, which reads the surface colour from its own wrapper
 * element, this reads it from the document root because the worker renders no element of its own.
 */
export const useHostTheme = (): {
	surfaceColor: string | null;
	theme: Partial<ThemeState> | null;
} => {
	// useThemeObserver returns a fresh object each time; memoize on deep equality (see ARKEN-856).
	const theme = useMemoizedStateObject(useThemeObserver());
	const [surfaceColor, setSurfaceColor] = useState<string | null>(null);

	useEffect(() => {
		const doc = getDocument();
		if (!doc) {
			return;
		}
		setSurfaceColor(
			getComputedStyle(doc.documentElement).getPropertyValue(CURRENT_SURFACE_CSS_VAR),
		);
	}, [theme]);

	return { theme, surfaceColor };
};
